import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useClerk } from '@clerk/react';
import ReactMarkdown from 'react-markdown';
import { Button, Card, H3, Body, BodySm } from '@/components/ui';
import { NotLegalIcon } from '@/components/ui/NotLegalIcon';
import { cn } from '@/lib/utils';
import { DEMO_USER, useDemoAuth } from '@/lib/demo-auth';
import { api } from '@/lib/api';
import { JurisdictionSelector } from '@/components/ui/JurisdictionSelector';
import { LawyerNudgeBanner } from '@/components/ui/LawyerNudgeBanner';
import { LawyerReferralCard } from '@/components/ui/LawyerReferralCard';
import { TokenBalanceChip } from '@/components/ui/TokenBalanceChip';
import { LowBalanceWarning } from '@/components/ui/LowBalanceWarning';
import { WelcomeBanner } from '@/components/features/WelcomeBanner';
import { ShareButton } from '@/components/ui/ShareButton';
import { SourcesSection } from '@/components/ui/SourcesSection';
import { checkForEscalation } from '@/lib/escalation';
import { parseCitations } from '@/lib/citationParser';

const effortOptions = [
  { id: 'fast', label: 'Fast', description: 'Quick & cheeky', tokenMultiplier: 0.5, temperature: 0.3 },
  { id: 'balanced', label: 'Balanced', description: 'Good balance', tokenMultiplier: 1.0, temperature: 0.7 },
  { id: 'deep', label: 'Deep', description: 'Maximum sass', tokenMultiplier: 1.5, temperature: 0.8 },
];

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export function ChatPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isDemo = searchParams.get('demo') === 'true';
  const navigate = useNavigate();
  const { user } = useClerk();
  const { user: demoUser, loginDemo, logoutDemo } = useDemoAuth();

  const [message, setMessage] = useState('');
  const [effort, setEffort] = useState<'fast' | 'balanced' | 'deep'>('balanced');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatSessions, setChatSessions] = useState<{ id: string; title: string; updatedAt: string }[]>([]);
  const [tokenBalance, setTokenBalance] = useState<number>(0);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [jurisdiction, setJurisdiction] = useState<string | null>(null);
  const [nudgeDismissed, setNudgeDismissed] = useState<Set<number>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Initialize chat
  useEffect(() => {
    if (isDemo && !demoUser) {
      loginDemo();
    }
  }, [isDemo, demoUser, loginDemo]);

  // Load demo data or check for pending question
  useEffect(() => {
    const initialQuestion = location.state?.initialQuestion as string | undefined;
    const queryQuestion = searchParams.get('q') as string | undefined;
    const pendingQuestion = localStorage.getItem('pendingQuestion');

    if (isDemo) {
      setTokenBalance(DEMO_USER.tokenBalance);
      setMessages([
        {
          role: 'assistant',
          content: "Hi there! I'm from notlegal.chat, where we specialize in *not legal advice*. I'm definitely not a lawyer, and I'm here to entertain, not advise. Anything I say should not be used in actual legal proceedings. With that out of the way, what legal question can I answer with absolutely no legal authority?"
        }
      ]);

      // Priority: query param > initialQuestion > localStorage
      if (queryQuestion || initialQuestion || pendingQuestion) {
        const question = queryQuestion || initialQuestion || pendingQuestion;
        if (question) {
          setMessage(question);
          localStorage.removeItem('pendingQuestion');
        }
      }
    } else {
      // Check for pending question (user was redirected from signup)
      if (pendingQuestion) {
        setMessage(pendingQuestion);
        localStorage.removeItem('pendingQuestion');
      }
      // Or use initial question from navigation
      if (initialQuestion) {
        setMessage(initialQuestion);
      }
      // Or use query param from URL (e.g., from landing page)
      if (queryQuestion) {
        setMessage(queryQuestion);
      }
    }
  }, [isDemo, demoUser, location.state, searchParams]);

  // Fetch token balance on mount for authenticated users
  useEffect(() => {
    if (!isDemo) {
      api.getUser().then(user => {
        if (user) setTokenBalance(user.tokenBalance ?? 0);
      }).catch(() => {});
    }
  }, [isDemo]);

  const autoSentRef = useRef(false);

  // Auto-send the initial question once (when arriving with a pre-filled question)
  useEffect(() => {
    if (!autoSentRef.current && !isDemo && message && messages.length > 0) {
      autoSentRef.current = true;
      const timer = setTimeout(() => {
        handleSend();
      }, 500);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once on mount

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Create a new chat session
  const createNewChat = useCallback(async () => {
    try {
      const response = await api.createChat();
      setCurrentChatId(response.id);
      setMessages([
        {
          role: 'assistant',
          content: "Hi there! I'm from notlegal.chat, where we specialize in *not legal advice*. I'm definitely not a lawyer, and I'm here to entertain, not advise. Anything I say should not be used in actual legal proceedings. With that out of the way, what legal question can I answer with absolutely no legal authority?"
        }
      ]);
      return response.id;
    } catch (error) {
      console.error('Error creating chat:', error);
      return null;
    }
  }, []);

  const handleJurisdictionChange = useCallback(async (code: string | null) => {
    setJurisdiction(code);
    if (currentChatId) {
      await api.updateChatJurisdiction(currentChatId, code).catch(() => {});
    }
  }, [currentChatId]);

  const handleSend = async () => {
    if (!message.trim() || isLoading) return;

    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    const userMsg = message;
    setMessage('');

    // Add user message immediately
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);

    try {
      const effortConfig = effortOptions.find(o => o.id === effort)!;

      if (isDemo) {
        // Demo mode - simulate response
        await new Promise(resolve => setTimeout(resolve, 1500));
        const responses = [
          "That's a great question! Unfortunately, I can't actually answer it legally, but here's my completely unqualified opinion: [AI response would appear here with full disclaimers]",
          "I see what you're asking. Before I give you completely non-legal advice: remember this is NOT legal advice. With that said... [response]",
          "Interesting! Here's the thing - I'm an AI, not an attorney. But if I were allowed to give legal advice (which I'm not), I might say something like: [content]",
        ];
        const randomResponse = responses[Math.floor(Math.random() * responses.length)];
        setMessages(prev => [...prev, { role: 'assistant', content: randomResponse }]);
      } else {
        // Production mode - call real API
        let chatId = currentChatId;
        if (!chatId) {
          chatId = await createNewChat();
        }

        if (!chatId) {
          throw new Error('Failed to create chat');
        }

        // Stream the response
        const responseText = await api.sendMessageStream(chatId, userMsg, effort);
        setMessages(prev => [...prev, { role: 'assistant', content: responseText }]);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Request was cancelled, do nothing
      } else {
        console.error('Chat error:', error);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: "Oops! Something went wrong. Remember, I'm just an AI providing entertainment, not legal advice. Please try again or consult a real lawyer for actual legal matters."
        }]);
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  return (
    <div className="min-h-screen bg-surface-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-surface-200 flex flex-col">
        <div className="p-4 border-b border-surface-200">
          <Link to="/" className="flex items-center space-x-2">
            <NotLegalIcon name="message" className="w-6 h-6 text-primary-600" />
            <span className="font-bold text-surface-900">notlegal.chat</span>
            {isDemo && (
              <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
                Demo
              </span>
            )}
          </Link>
        </div>

        <div className="p-4">
          <Button
            className="w-full"
            icon={<NotLegalIcon name="plus" className="w-4 h-4" />}
            onClick={createNewChat}
          >
            New Chat
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          <BodySm className="text-surface-500 mb-2">Recent Chats</BodySm>
          {chatSessions.length === 0 ? (
            <BodySm className="text-surface-400 text-center py-4">
              {isDemo ? 'No chat history in demo mode' : 'Start a chat to see history here'}
            </BodySm>
          ) : (
            chatSessions.map((session) => (
              <button
                key={session.id}
                className="w-full text-left px-4 py-3 rounded-xl hover:bg-surface-100 transition-colors text-sm"
              >
                <div className="font-medium text-surface-700 truncate">{session.title}</div>
                <div className="text-xs text-surface-400 mt-1">{session.updatedAt}</div>
              </button>
            ))
          )}
        </div>

        <div className="p-4 border-t border-surface-200 space-y-2">
          <Link to="/documents" className="flex items-center space-x-3 px-4 py-2 text-surface-600 hover:bg-surface-100 rounded-xl transition-colors">
            <NotLegalIcon name="document" className="w-4 h-4" />
            <span className="text-sm">Documents</span>
          </Link>
          <Link to="/pricing" className="flex items-center space-x-3 px-4 py-2 text-surface-600 hover:bg-surface-100 rounded-xl transition-colors">
            <NotLegalIcon name="creditCard" className="w-4 h-4" />
            <span className="text-sm">Upgrade</span>
          </Link>
        </div>

        <div className="p-4 border-t border-surface-200">
          {isDemo ? (
            <div className="space-y-3">
              <div className="flex items-center space-x-3 px-4 py-2">
                <img
                  src={DEMO_USER.avatarUrl}
                  alt="Demo"
                  className="w-8 h-8 rounded-full"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-surface-700 truncate">
                    {DEMO_USER.displayName}
                  </div>
                  <div className="text-xs text-surface-400 truncate">
                    {DEMO_USER.subscriptionTier} plan
                  </div>
                </div>
              </div>
              <Link to="/" onClick={logoutDemo} className="flex items-center space-x-2 px-4 py-2 text-surface-500 hover:text-red-600 transition-colors text-sm">
                <NotLegalIcon name="logout" className="w-4 h-4" />
                <span>Exit Demo</span>
              </Link>
            </div>
          ) : user ? (
            <div className="space-y-3">
              <div className="flex items-center space-x-3 px-4 py-2">
                {user.imageUrl ? (
                  <img src={user.imageUrl} alt={user.firstName || 'User'} className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <NotLegalIcon name="user" className="w-4 h-4 text-primary-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-surface-700 truncate">
                    {user.firstName || 'User'}
                  </div>
                  <div className="text-xs text-surface-400 truncate">
                    {tokenBalance > 0 ? `${tokenBalance.toLocaleString()} tokens` : 'No tokens'}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Link to="/sign-in" className="flex items-center space-x-3 px-4 py-2 text-surface-600 hover:bg-surface-100 rounded-xl transition-colors">
              <NotLegalIcon name="user" className="w-4 h-4" />
              <span className="text-sm">Sign In</span>
            </Link>
          )}
        </div>
      </aside>

      {/* Main Chat Area */}
      <main className="flex-1 flex flex-col">
        {/* Chat Header */}
        <header className="bg-white border-b border-surface-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <H3>New Chat</H3>
            <span className="text-sm text-surface-500">
              ~{Math.round(100 * effortOptions.find(o => o.id === effort)!.tokenMultiplier)} tokens/message
            </span>
            <TokenBalanceChip balance={tokenBalance} />
            <JurisdictionSelector value={jurisdiction} onChange={handleJurisdictionChange} compact={true} />
          </div>
          <div className="flex items-center space-x-2">
            {effortOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => setEffort(option.id as 'fast' | 'balanced' | 'deep')}
                className={cn(
                  'px-4 py-2 rounded-full text-sm font-medium transition-all',
                  effort === option.id
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-surface-600 hover:bg-surface-100'
                )}
              >
                {option.id === 'fast' && '⚡'}
                {option.id === 'balanced' && '⚖️'}
                {option.id === 'deep' && '🧠'}
                {' '}{option.label}
              </button>
            ))}
          </div>
        </header>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-6">
          <WelcomeBanner balance={tokenBalance} />
          <div className="max-w-3xl mx-auto space-y-6">
            {messages.map((msg, index) => {
              const isAssistant = msg.role === 'assistant';
              const { cleanedText, sources } = isAssistant ? parseCitations(msg.content) : { cleanedText: msg.content, sources: [] };
              const escalation = isAssistant ? checkForEscalation(cleanedText) : null;
              const displayText = escalation ? escalation.cleanedText : cleanedText;

              return (
                <div key={index} className="flex items-start space-x-4">
                  <div className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                    isAssistant ? 'bg-primary-600' : 'bg-surface-300'
                  )}>
                    {isAssistant ? (
                      <NotLegalIcon name="bot" className="w-5 h-5 text-white" />
                    ) : (
                      <NotLegalIcon name="user" className="w-5 h-5 text-surface-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <Card variant="elevated">
                      <div className="text-surface-700 prose prose-sm max-w-none">
                        <ReactMarkdown>{displayText}</ReactMarkdown>
                      </div>
                      {isAssistant && (
                        <>
                          <SourcesSection sources={sources} />
                          {escalation?.shouldNudge && !nudgeDismissed.has(index) && (
                            <LawyerNudgeBanner
                              category={escalation.category}
                              onDismiss={() => setNudgeDismissed(prev => new Set([...prev, index]))}
                              onFindLawyer={() => {
                                api.trackReferralClick(currentChatId ?? undefined);
                                window.open('https://www.avvo.com/find-a-lawyer', '_blank');
                              }}
                            />
                          )}
                          <LawyerReferralCard
                            variant={escalation?.shouldNudge ? 'prominent' : 'subtle'}
                            jurisdiction={jurisdiction}
                            category={escalation?.category}
                            onTrackClick={() => api.trackReferralClick(currentChatId ?? undefined)}
                          />
                          <div className="mt-3 pt-3 border-t border-surface-100 flex items-center justify-between">
                            <BodySm className="text-surface-500 flex items-center">
                              <NotLegalIcon name="sparkle" className="w-3 h-3 mr-1" />
                              This message is not legal advice. It's just AI-generated entertainment.
                            </BodySm>
                            <ShareButton
                              onShare={async () => {
                                const userMsg = messages[index - 1]?.content ?? '';
                                return api.createShare({
                                  chatId: currentChatId,
                                  question: userMsg,
                                  answer: msg.content,
                                  jurisdiction,
                                });
                              }}
                            />
                          </div>
                        </>
                      )}
                    </Card>
                  </div>
                </div>
              );
            })}

            {isLoading && (
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0 animate-pulse">
                  <NotLegalIcon name="bot" className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <Card variant="elevated">
                    <div className="flex items-center space-x-2 text-surface-500">
                      <div className="w-2 h-2 bg-surface-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-surface-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-surface-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      <span className="text-sm ml-2">Thinking about not giving legal advice...</span>
                    </div>
                  </Card>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input Area */}
        <div className="bg-white border-t border-surface-200 p-6">
          <LowBalanceWarning balance={tokenBalance} onTopUp={() => navigate('/billing')} />
          <div className="max-w-3xl mx-auto">
            {/* Generate Document button - shows when chat has messages */}
            {messages.length > 1 && currentChatId && (
              <div className="flex justify-center mb-3">
                <Link to={`/documents/generate?chatId=${currentChatId}`}>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<NotLegalIcon name="document" className="w-4 h-4" />}
                  >
                    Generate Legal Document
                  </Button>
                </Link>
              </div>
            )}
            <div className="relative">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Ask me anything legal... that I'm not allowed to answer legally..."
                className="w-full px-6 py-4 pr-32 bg-surface-100 border-2 border-surface-200 rounded-2xl text-surface-900 placeholder:text-surface-400 focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all resize-none"
                rows={3}
                disabled={isLoading}
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center space-x-2">
                <span className="text-xs text-surface-400 hidden sm:inline">
                  ~{Math.round(100 * effortOptions.find(o => o.id === effort)!.tokenMultiplier)} tokens
                </span>
                <Button
                  size="sm"
                  icon={<NotLegalIcon name="send" className="w-4 h-4" />}
                  onClick={handleSend}
                  disabled={isLoading || !message.trim()}
                >
                  Send
                </Button>
              </div>
            </div>
            <BodySm className="mt-3 text-center text-surface-500">
              Your message and our response will include disclaimers. Because liability.
            </BodySm>
          </div>
        </div>
      </main>
    </div>
  );
}
